import { useState, useEffect, useRef, useMemo } from 'react'
import { TYPEWRITER_SPEED } from '../../utils/typewriterSpeed'

interface TypewriterTextProps {
  text: string
  speed?: number // ミリ秒単位での表示速度
  onComplete?: () => void
  style?: React.CSSProperties
  className?: string
  resetTrigger?: any // この値が変わると表示をリセット
  skipAnimation?: boolean // true の場合は即座に全文表示
}

export function TypewriterText({
  text,
  speed = TYPEWRITER_SPEED.default,
  onComplete,
  style,
  className,
  resetTrigger,
  skipAnimation = false
}: TypewriterTextProps) {
  // skipAnimationがtrueの場合は状態管理をスキップ
  const [displayText, setDisplayText] = useState(skipAnimation ? text : '')
  const [currentIndex, setCurrentIndex] = useState(skipAnimation ? text.length : 0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const hasCalledOnComplete = useRef(false)

  // resetTriggerまたはtextが変わったら表示をリセット
  useEffect(() => {
    hasCalledOnComplete.current = false
    if (skipAnimation) {
      // アニメーションをスキップして即座に全文表示
      setDisplayText(text)
      setCurrentIndex(text.length)
    } else {
      setDisplayText('')
      setCurrentIndex(0)
    }
  }, [text, resetTrigger]) // skipAnimationは依存配列から除外

  // onCompleteコールバックを別のuseEffectで管理
  useEffect(() => {
    if (currentIndex === text.length && text.length > 0 && onComplete && !hasCalledOnComplete.current) {
      hasCalledOnComplete.current = true
      onComplete()
    }
  }, [currentIndex, text.length]) // onCompleteは依存配列から除外

  // 一文字ずつ表示（アニメーションがスキップされていない場合のみ）
  useEffect(() => {
    if (skipAnimation) {
      return // アニメーションスキップ時は何もしない
    }

    if (currentIndex < text.length) {
      intervalRef.current = setTimeout(() => {
        // 文字を一つずつ追加（charAt使用で安全にアクセス）
        const nextChar = text.charAt(currentIndex)
        setDisplayText(prev => prev + nextChar)
        setCurrentIndex(prev => prev + 1)
      }, speed)
    }

    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current)
      }
    }
  }, [currentIndex, text, speed, skipAnimation])

  return (
    <span className={className} style={style}>
      {displayText}
      {!skipAnimation && currentIndex < text.length && (
        <span className="animate-pulse">_</span>
      )}
    </span>
  )
}
